import React, { useRef, useState, useEffect, useCallback } from 'react';
import { testId } from '../../../../utils/testIds';
import { RENDER_MODE } from '../../constants';
import { uploadFileOnPublicApiV2, shortFileName, getAxiosError } from '@common/util';
import Icons from '@icons';
import FileRow from './FileRow';
import {
  ATTACH_FILES,
  VALID_FILE,
  INVALID_FILE_EXTENSION,
  INVALID_FILE_SIZE,
  INVALID_FILE_EXTENSION_ERROR,
  INVALID_FILE_SIZE_ERROR,
  SERVER_UNAVAILABLE_ERROR_MESSAGE,
  MALICIOUS_OR_VIRUS_INFECTED_ERROR,
} from '@components/PublicApiExzeoUpload/constants';
import './styles.scss';

const DEFAULT_MAX_FILES     = 64;
const DEFAULT_MAX_FILE_SIZE = 99 * 1024 * 1024; // 99 MB
const DEFAULT_INVALID_FILES = (ext) => String(ext).toLowerCase() === 'exe';
const MAX_FILES_ERROR_PREFIX = 'You can attach a maximum of';

function getExtension(name) {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function buildFileUrl(guid, name) {
  const ext = getExtension(name);
  let scheme = 'http';
  let host = '';
  try {
    if (typeof process !== 'undefined' && process.env) {
      scheme = process.env.UrlScheme || scheme;
      host = process.env.RootUrl || process.env.siteUrl || '';
    }
  } catch (e) { /*  */ }
  return `${scheme}://${host}/gpu?fileName=${guid}.${ext}`;
}

function humanSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return '';
  if (n >= 1048576) return `${Math.round(n / 1048576)} MB`;
  if (n >= 1024)    return `${Math.round(n / 1024)} KB`;
  return `${Math.round(n)} bytes`;
}

function LegacyFileField({ config, value, onChange, onBlur, disabled, required, error, renderMode }) {
  const inputRef = useRef(null);
  const files    = Array.isArray(value) ? value : [];

  if (renderMode === RENDER_MODE.CARD || renderMode === RENDER_MODE.GRID) {
    return (
      <span className="field-engine-file-value">
        {files.length > 0
          ? `${files.length} file${files.length > 1 ? 's' : ''}`
          : <span className="field-engine-empty">—</span>}
      </span>
    );
  }

  if (renderMode === RENDER_MODE.DETAIL) {
    return (
      <div className="field-engine-file-detail">
        {files.map((f, i) => (
          <a key={i} href={f.url} className="field-engine-file-link" target="_blank" rel="noopener noreferrer">
            {f.name}
          </a>
        ))}
      </div>
    );
  }

  function handleFileChange(e) {
    const newFiles = Array.from(e.target.files).map(f => ({
      name: f.name,
      size: f.size,
      file: f,
    }));
    onChange([...files, ...newFiles]);
  }

  function removeFile(index) {
    onChange(files.filter((_, i) => i !== index));
  }

  return (
    <div className={`field-engine-file-upload${error ? ' field-engine-file-upload--error' : ''}${disabled ? ' disabled' : ''}`}>
      <input
        ref={inputRef}
        id={config.fieldKey}
        data-test={testId('field', config.fieldKey, 'input')}
        name={config.fieldKey}
        type="file"
        multiple
        className="field-engine-file-input field-engine-file-hidden-input"
        onChange={handleFileChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-required={required}
        aria-invalid={!!error}
      />
      <span
        role="button"
        data-test={testId('field', config.fieldKey, 'attach')}
        tabIndex={disabled ? -1 : 0}
        className={`field-engine-file-link-btn${disabled ? ' field-engine-file-link-btn--disabled' : ''}`}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !disabled && inputRef.current?.click()}
      >
        Attach Files
      </span>
      {files.length > 0 && (
        <ul className="field-engine-file-list">
          {files.map((f, i) => (
            <li key={i} className="field-engine-file-item">
              <span>{f.name}</span>
              {!disabled && (
                <button type="button" onClick={() => removeFile(i)} className="field-engine-file-remove">
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UploaderFileField({ config, value, onChange, onBlur, disabled, required, error, renderMode }) {
  const settings     = config?.fieldSettings ?? {};
  const uploader     = settings.uploader     ?? uploadFileOnPublicApiV2;
  const isSingleFile = !!settings.isSingleFile;
  const maxFiles     = settings.maxFiles     ?? DEFAULT_MAX_FILES;
  const maxSize      = settings.maxSize      ?? DEFAULT_MAX_FILE_SIZE;
  const invalidFiles = settings.invalidFiles ?? DEFAULT_INVALID_FILES;
  const helpText     = settings.helpText     ?? '';

  const inputRef  = useRef(null);
  const tempIdRef = useRef(0);
  const [rows, setRows]     = useState(() => (Array.isArray(value) ? value : []));
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (Array.isArray(value) && value.length && rows.length === 0) {
      setRows(value);
    }
  }, [value]);

  const publishUploaded = useCallback((nextRows) => {
    onChange(nextRows.filter((r) => !r.isLoading && r.guid));
  }, [onChange]);

  if (renderMode === RENDER_MODE.CARD || renderMode === RENDER_MODE.GRID || renderMode === RENDER_MODE.DETAIL) {
    return (
      <LegacyFileField
        config={config}
        value={rows}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        error={error}
        renderMode={renderMode}
      />
    );
  }

  function validateFile(f) {
    if (!f) return VALID_FILE;
    const ext = getExtension(f.name);
    if (invalidFiles(ext)) return INVALID_FILE_EXTENSION;
    if (f.size > maxSize)  return INVALID_FILE_SIZE;
    return VALID_FILE;
  }

  function pushErrors(newErrs) {
    if (!newErrs.length) return;
    setErrors((prev) => [...prev, ...newErrs]);
  }

  async function handleSelectedFiles(e) {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    setErrors([]);
    const cleanedRows = rows.filter((r) => !r.isLoading);

    const existingCount  = isSingleFile ? 0 : cleanedRows.length;
    const remainingSlots = Math.max(0, maxFiles - existingCount);

    const newErrors    = [];
    const validPairs   = [];
    const placeholders = [];

    picked.forEach((file) => {
      if (validPairs.length >= remainingSlots) {
        newErrors.push({
          id: `err-${++tempIdRef.current}`,
          name: file.name,
          error: `${MAX_FILES_ERROR_PREFIX} ${maxFiles} file(s).`,
        });
        return;
      }
      const status = validateFile(file);
      if (status === VALID_FILE) {
        const tempId = ++tempIdRef.current;
        placeholders.push({ tempId, fileName: file.name, size: file.size, isLoading: true });
        validPairs.push({ tempId, file });
      } else if (status === INVALID_FILE_EXTENSION) {
        newErrors.push({
          id: `err-${++tempIdRef.current}`,
          name: file.name,
          error: INVALID_FILE_EXTENSION_ERROR,
        });
      } else if (status === INVALID_FILE_SIZE) {
        newErrors.push({
          id: `err-${++tempIdRef.current}`,
          name: file.name,
          error: `${INVALID_FILE_SIZE_ERROR} ${humanSize(maxSize)}`,
        });
      }
    });

    pushErrors(newErrors);
    if (!validPairs.length) return;

    let workingRows = isSingleFile ? [...placeholders] : [...cleanedRows, ...placeholders];
    setRows(workingRows);

    const formData = new FormData();
    validPairs.forEach(({ file }) => {
      formData.append('file', file, file.name);
    });

    let res;
    try {
      res = await uploader(formData);
    } catch (err) {
      console.error('[FileField v2 upload] network failed:', err, 'response:', err?.response);
      const parsed = getAxiosError(err);
      workingRows = workingRows.filter((r) => !validPairs.some(({ tempId }) => tempId === r.tempId));
      setRows(workingRows);
      pushErrors(validPairs.map(({ tempId, file }) => ({
        id: `err-${tempId}`,
        name: file.name,
        error: (parsed && parsed.error) || SERVER_UNAVAILABLE_ERROR_MESSAGE,
      })));
      return;
    }

    try {
      const result = res && res.status === 200 && res.data && Array.isArray(res.data.result)
        ? res.data.result
        : null;

      if (!result) {
        workingRows = workingRows.filter((r) => !validPairs.some(({ tempId }) => tempId === r.tempId));
        setRows(workingRows);
        pushErrors(validPairs.map(({ tempId, file }) => ({
          id: `err-${tempId}`,
          name: file.name,
          error: res?.statusText || SERVER_UNAVAILABLE_ERROR_MESSAGE,
        })));
        return;
      }

      const uploadedByTempId = new Map();
      validPairs.forEach(({ tempId, file }, i) => {
        const entry = result[i];
        if (!entry || !entry.guid) return;
        uploadedByTempId.set(tempId, {
          guid: entry.guid,
          name: entry.file_name || file.name,
          size: file.size,
          caption: entry.caption || '',
          url: buildFileUrl(entry.guid, entry.file_name || file.name),
        });
      });

      workingRows = workingRows
        .map((r) => (r.tempId && uploadedByTempId.has(r.tempId) ? uploadedByTempId.get(r.tempId) : r))
        .filter((r) => !(r.isLoading && r.tempId && !uploadedByTempId.has(r.tempId)));

      const missing = validPairs.filter(({ tempId }) => !uploadedByTempId.has(tempId));
      if (missing.length) {
        pushErrors(missing.map(({ tempId, file }) => ({
          id: `err-${tempId}`,
          name: file.name,
          error: SERVER_UNAVAILABLE_ERROR_MESSAGE,
        })));
      }

      setRows(workingRows);
    } catch (mapErr) {
      console.error('[FileField v2 upload] mapping threw:', mapErr);
    }

    try {
      publishUploaded(workingRows);
    } catch (err) {
      console.error('[FileField v2 upload] onChange threw (upload succeeded):', err);
    }
  }

  function handleRemoveFile(row) {
    const next = rows.filter((r) =>
      r.file_id !== row.file_id || r.guid !== row.guid
    );
    setRows(next);
    publishUploaded(next);
  }

  function handleUpdateCaption(guid, caption) {
    const next = rows.map((r) => (r.guid === guid ? { ...r, caption } : r));
    setRows(next);
    publishUploaded(next);
  }

  function handleRemoveError(id) {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  }

  const completedCount = rows.filter((r) => !r.isLoading && r.guid).length;
  const attachDisabled = disabled || completedCount >= maxFiles;

  return (
    <div className={`file_uploader hasMultipleFileUpload${disabled ? ' disabled' : ''}`}>
      <div>
        <div className="rowFlex uploadButton" role="button">
          <span>
            <a
              data-test={testId('field', config.fieldKey, 'attach')}
              className="mrNone"
              href="javascript:void(0)"
              onClick={(e) => e.preventDefault()}
            >
              <Icons name="attach" className="icon mr" />
              {isSingleFile ? 'Attach file' : ATTACH_FILES}
            </a>
            <input
              ref={inputRef}
              data-test={testId('field', config.fieldKey, 'input')}
              type="file"
              title={isSingleFile ? '' : ATTACH_FILES}
              name={config.fieldKey}
              multiple={!isSingleFile}
              className="fileupload"
              onChange={handleSelectedFiles}
              onClick={(e) => { e.currentTarget.value = ''; }}
              onBlur={onBlur}
              disabled={attachDisabled}
              aria-required={required}
              aria-invalid={!!error}
            />
          </span>
          {helpText && (
            <span className="quickTooltip-top" data-quicktooltip={helpText}>
              <Icons name="info" className="icon ml greyInfoIcon" />
            </span>
          )}
        </div>

        {(rows.length > 0 || errors.length > 0) && (
          <>
            <div className="bo mt">
              {completedCount > 0
                ? `You have selected ${completedCount} ${isSingleFile ? 'file' : 'file(s)'}.`
                : ''}
            </div>
            <div className="fileUploadMain">
              <ul>
                {errors.map((item) => (
                  <li key={item.id}>
                    <div className="uploadImage errorUpload">
                      <Icons
                        name={
                          item.error === MALICIOUS_OR_VIRUS_INFECTED_ERROR ? 'maliciousFile'
                          : item.error === SERVER_UNAVAILABLE_ERROR_MESSAGE ? 'reset'
                          : 'errorcircle'
                        }
                        className="icon"
                      />
                    </div>
                    <div className="uploadImageInfo">
                      <span className="fileName" title={item.name}>{shortFileName(item.name)}</span>
                      <span className="errorSelfMsg">{item.error}</span>
                      <div onClick={() => handleRemoveError(item.id)}>
                        <Icons name="closeFilled" className="icon close" />
                      </div>
                    </div>
                  </li>
                ))}
                {rows.map((row, idx) => (
                  <FileRow
                    key={row.guid || `t-${row.tempId}` || idx}
                    data={row}
                    itemIndex={idx}
                    onUpdateCaption={handleUpdateCaption}
                    onRemove={handleRemoveFile}
                  />
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FileField(props) {
  const hasUploader = typeof props?.config?.fieldSettings?.uploader === 'function';
  if (!hasUploader) return <LegacyFileField {...props} />;
  return <UploaderFileField {...props} />;
}

export default FileField;
